/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosPromise,
  AxiosError,
} from "axios"
import * as transformData from "axios/lib/core/transformData"
import * as defaults from "axios/lib/defaults"

import { canUseDOM } from "utils/dom"
import { cleanCookiesStorage } from "utils/local-storage"

import { statusToCode } from "./statusToCode"

const forceLogout = (url?: string, method?: string) => {
  if (canUseDOM()) {
    cleanCookiesStorage()

    void (async () => {
      const mixpanelV2Client = (await import("services/mixpanelv2"))
        .mixpanelV2Client
      const mixpanelV1Client = (await import("services/mixpanel"))
        .mixpanelV1Client
      mixpanelV2Client.RESET_USER()
      mixpanelV1Client.LOGOUT({
        logoutKey: "Force Logout",
        url,
        method,
      })
    })()
    window.location.href = "/"
  }
}

function settle(
  resolve: (
    value:
      | AxiosResponse<unknown, unknown>
      | PromiseLike<AxiosResponse<unknown, unknown>>,
  ) => void,
  reject: (reason?: unknown) => void,
  response: AxiosResponse,
) {
  const { validateStatus } = response.config

  if (!response.status || !validateStatus || validateStatus(response.status)) {
    resolve(response)
  } else {
    reject(
      new AxiosError(
        response.data.errorMessage || response?.data?.data?.message,
        response.data.errorCode,
        response.config,
        response.request,
        response,
      ),
    )
  }
}

export const customAdapter = (
  config: AxiosRequestConfig,
): AxiosPromise<unknown> => {
  return new Promise((resolve, reject) => {
    defaults
      .adapter(config)
      .then((response: AxiosResponse) => {
        if (response?.status === 401) {
          forceLogout(response.config.url, response.config.method)
        }

        if (response?.status === 429) {
          reject(
            new AxiosError(
              "Too many requests",
              "429",
              response?.config,
              response?.request,
              response,
            ),
          )
        }

        // unfortunately data is transformed after the adapter
        // eslint-disable-next-line no-param-reassign
        response.data = transformData(
          response.data,
          response.headers,
          config.transformResponse,
        )

        if (
          (response.status === 200 ||
            response.status === 201 ||
            response.status === 202 ||
            response.status === 203 ||
            response.status === 204) &&
          response.data.status
        ) {
          // eslint-disable-next-line no-param-reassign
          response.status = statusToCode(response.data.status)
        }

        settle(resolve, reject, response)
        return
      })
      .catch((e: AxiosError) => {
        const response = e.response as AxiosResponse
        if (response?.status === 401) {
          forceLogout(response.config.url, response.config.method)
        }

        if (response?.status === 429) {
          reject(
            new AxiosError(
              "Too many requests",
              "429",
              response?.config,
              response?.request,
              response,
            ),
          )
        }

        const responseData =
          response?.data && response?.data.startsWith("{")
            ? JSON.parse(response?.data)
            : response?.data

        reject(
          new AxiosError(
            responseData?.errorMessage || responseData?.message || e?.message,
            responseData?.errorCode || e?.code,
            response?.config,
            response?.request,
            response,
          ),
        )
      })
  })
}
